import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';

const LoadingContext = createContext();

// Global listener for non-React callers like Axios interceptors
let globalLoadingListener = null;

export const registerLoadingListener = (listener) => {
  globalLoadingListener = listener;
  return () => {
    if (globalLoadingListener === listener) {
      globalLoadingListener = null;
    }
  };
};

export const triggerGlobalLoadingStart = (msg) => {
  if (globalLoadingListener?.start) {
    globalLoadingListener.start(msg);
  }
};

export const triggerGlobalLoadingStop = () => {
  if (globalLoadingListener?.stop) {
    globalLoadingListener.stop();
  }
};

/**
 * Hook to access the global loading state.
 */
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const counterRef = useRef(0);
  const startTimeRef = useRef(0);
  const hideTimerRef = useRef(null);

  const startLoading = useCallback((msg = '') => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    if (counterRef.current === 0) {
      startTimeRef.current = Date.now();
      setIsLoading(true);
    }
    counterRef.current += 1;
    if (msg) {
      setMessage(msg);
    }
  }, []);

  const stopLoading = useCallback(() => {
    counterRef.current = Math.max(0, counterRef.current - 1);
    if (counterRef.current === 0) {
      // Keep loader visible for a minimum of 450ms so fast API calls don't flicker or flash invisibly
      const elapsed = Date.now() - startTimeRef.current;
      const minDisplayDuration = 450;
      const delay = Math.max(0, minDisplayDuration - elapsed);

      hideTimerRef.current = setTimeout(() => {
        if (counterRef.current === 0) {
          setIsLoading(false);
          setMessage('');
        }
      }, delay);
    }
  }, []);

  const withLoading = useCallback(
    async (asyncFn, msg = '') => {
      startLoading(msg);
      try {
        return await asyncFn();
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading]
  );

  // Subscribe Axios interceptors to this provider
  useEffect(() => {
    const unregister = registerLoadingListener({
      start: startLoading,
      stop: stopLoading,
    });
    return () => {
      unregister();
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [startLoading, stopLoading]);

  // Window helper for quick manual testing in browser console: window.__testGlobalLoader(2000)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__testGlobalLoader = (duration = 2000, msg = 'Fetching latest data...') => {
        startLoading(msg);
        setTimeout(() => stopLoading(), duration);
      };
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.__testGlobalLoader;
      }
    };
  }, [startLoading, stopLoading]);

  const value = {
    isLoading,
    message,
    startLoading,
    stopLoading,
    withLoading,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};
